/**
 * idbStatus.ts
 *
 * Module-level singleton that tracks whether IndexedDB is usable.
 *
 * THREE STATES:
 *   "available"   — IDB opens and writes succeed. Normal IDB-first flow.
 *   "unavailable" — IDB cannot open at all (Safari private mode, disabled storage,
 *                   browser quirk). All reads and writes will fail.
 *   "quota_full"  — IDB opens and reads work, but writes fail with QuotaExceededError.
 *                   Existing cached data is still readable.
 *
 * WHY a module-level variable (not React context or useState):
 *   - Mutation functions (addBlockMutation, updateBlockContentMutation, etc.) are
 *     plain async functions outside React — they cannot read a React context.
 *   - SyncWatcher is a singleton class running outside React — same constraint.
 *   - React context requires a mounted component. This variable is readable the
 *     instant any module imports it, before any component renders.
 *   - As a module, it is a singleton — every import across the whole app shares
 *     the exact same variable instance.
 *
 * USAGE:
 *   import { isIDBAvailable, getIDBStatus, setIDBStatus } from "@/indexedDB/idbStatus";
 *
 *   // In a mutation:
 *   if (!isIDBAvailable()) { // fall back to direct API call }
 *
 *   // In SyncWatcher:
 *   if (getIDBStatus() === "unavailable") return;
 *
 *   // When a write fails mid-session (quota fills up):
 *   setIDBStatus("quota_full");
 */

export type IDBStatus = "available" | "unavailable" | "quota_full";

/**
 * Returns true if the error is a storage quota exhaustion error.
 * Dexie wraps native DOMExceptions so the real error may be in err.inner or err.cause.
 * Import this in every mutation file's catch block.
 */
export function isQuotaError(err: any): boolean {
  const name: string = err?.name ?? err?.inner?.name ?? err?.cause?.name ?? "";
  const msg: string  = (err?.message ?? "").toLowerCase();
  return (
    name === "QuotaExceededError" ||
    msg.includes("quota") ||
    msg.includes("storage full") ||
    msg.includes("not enough storage")
  );
}

// Optimistic default — assume IDB works until the probe says otherwise.
// This means the very first render can attempt IDB reads without waiting for
// the probe. If the probe returns a non-available status, subsequent operations
// will see the updated value.
let _idbStatus: IDBStatus = "available";

/** Read the current IDB status from anywhere in the app. */
export function getIDBStatus(): IDBStatus {
  return _idbStatus;
}

/**
 * Returns true only when IDB is fully available for both reads and writes.
 * Shorthand for getIDBStatus() === "available".
 */
export function isIDBAvailable(): boolean {
  return _idbStatus === "available";
}

/**
 * Update the IDB status. Call this:
 *   - From probeIDB() at app startup.
 *   - From any mutation's catch block when a QuotaExceededError is caught
 *     mid-session (disk fills up while the app is running).
 */
export function setIDBStatus(status: IDBStatus): void {
  if (_idbStatus === status) return; // no-op if unchanged
  const previous = _idbStatus;
  _idbStatus = status;
  console.warn(
    `%c[IDB] Status changed: ${previous} → ${status}`,
    "color: #f87171; font-weight: bold;"
  );
}

/**
 * probeIDB — run once at app startup to determine the real IDB status.
 *
 * The probe does three things in order:
 *   1. Explicitly opens the Dexie database. If this throws, IDB is unavailable
 *      (bad browser, private mode, disabled storage).
 *   2. Executes a write → delete inside a transaction. If the write throws
 *      QuotaExceededError, the disk is full and we cannot cache anything.
 *   3. If both succeed, IDB is fully available.
 *
 * The probe writes to the dedicated idbProbe store using a sentinel key
 * "__idb_probe__" and immediately deletes it — leaving no trace in the
 * database. A separate store keeps syncMeta strictly typed (its `id` field is
 * the literal "global") and avoids any contention with normal sync writes.
 *
 * Fire-and-forget from the app entry point:
 *   probeIDB().then(() => syncWatcher.initialize(workspaceId));
 *
 * Or await it if you need the result before proceeding:
 *   const status = await probeIDB();
 */
export async function probeIDB(): Promise<IDBStatus> {
  try {
    // Dynamic import avoids a circular dependency between idbStatus ↔ client.
    const { appDB } = await import("./db/client");

    // ── Step 1: Open the database ─────────────────────────────────────────────
    // Dexie opens lazily; calling open() explicitly surfaces any open failure
    // (InvalidStateError, blocked, version mismatch, etc.) right here.
    await appDB.open();

    // ── Step 2: Test write capability ─────────────────────────────────────────
    // A tiny write → delete transaction. If the disk is full, the put() call
    // throws QuotaExceededError and the transaction rolls back automatically.
    // The delete() ensures we leave no probe record behind on success.
    const PROBE_ID = "__idb_probe__";
    await appDB.transaction("rw", appDB.idbProbe, async () => {
      await appDB.idbProbe.put({ id: PROBE_ID, ts: Date.now() });
      await appDB.idbProbe.delete(PROBE_ID);
    });

    // ── All good ──────────────────────────────────────────────────────────────
    setIDBStatus("available");
    console.log(
      "%c[IDB] ✅ Probe passed — IndexedDB is available",
      "color: #4ade80; font-weight: bold;"
    );
    return "available";

  } catch (err: any) {
    // Dexie wraps native errors; the real DOMException may be in err.inner.
    const errorName: string =
      err?.name ?? err?.inner?.name ?? err?.cause?.name ?? "";
    const errorMsg: string = err?.message ?? "";

    if (
      errorName === "QuotaExceededError" ||
      errorMsg.toLowerCase().includes("quota") ||
      errorMsg.toLowerCase().includes("storage")
    ) {
      // IDB opened but storage is full — reads still work.
      setIDBStatus("quota_full");
      console.warn(
        "%c[IDB] ⚠️ Probe failed — storage quota exceeded. Reads still work; writes will fall back to API.",
        "color: #fb923c; font-weight: bold;"
      );
      return "quota_full";
    }

    // Anything else (InvalidStateError, OpenFailedError, etc.) — IDB not usable.
    setIDBStatus("unavailable");
    console.warn(
      "%c[IDB] ❌ Probe failed — IndexedDB unavailable. All operations will go directly to API.",
      "color: #f87171; font-weight: bold;",
      err
    );
    return "unavailable";
  }
}
